import React from 'react';
import { Eye, Focus, Clock, Zap, Volume2, Target } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

interface FocusActivity {
  id: string;
  title: string;
  description: string;
  type: 'visual_tracking' | 'pattern_memory' | 'calm_breathing' | 'delay_gratification' | 'audio_sequencing' | 'stillness_focus';
  duration: number; // in seconds
  difficulty: 'easy' | 'medium' | 'hard';
  points_reward: number;
}

interface FocusActivityCardProps {
  activity: FocusActivity;
  onStart: () => void;
  completed?: boolean;
}

const activityIcons = {
  visual_tracking: Eye,
  pattern_memory: Target,
  calm_breathing: Volume2,
  delay_gratification: Clock,
  audio_sequencing: Volume2,
  stillness_focus: Focus,
};

const activityColors = {
  visual_tracking: 'cyan',
  pattern_memory: 'purple',
  calm_breathing: 'green',
  delay_gratification: 'orange',
  audio_sequencing: 'blue',
  stillness_focus: 'teal',
};

const difficultyColors = {
  easy: 'green',
  medium: 'yellow',
  hard: 'red',
};

export function FocusActivityCard({ activity, onStart, completed = false }: FocusActivityCardProps) {
  const { theme } = useTheme();
  const IconComponent = activityIcons[activity.type];
  const colorClass = activityColors[activity.type];
  const difficultyColor = difficultyColors[activity.difficulty];

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m`;
  };

  return (
    <div className={`rounded-2xl shadow-lg border-2 overflow-hidden transition-all duration-200 flex flex-col h-full ${
      theme.reducedMotion ? '' : 'hover:scale-105 hover:shadow-xl'
    } ${
      theme.mode === 'high-contrast'
        ? 'bg-white border-black'
        : 'bg-[var(--bg-secondary)] border-[var(--border-color)]'
    }`}>
      {/* Header with gradient */}
      <div className={`h-3 ${
        theme.mode === 'high-contrast'
          ? `bg-[var(--accent-${colorClass})]`
          : `bg-gradient-to-r from-${colorClass}-400 to-${colorClass}-600`
      }`}></div>
      
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className={`p-3 rounded-xl flex-shrink-0 ${
              theme.mode === 'high-contrast'
                ? `bg-[var(--accent-${colorClass})]`
                : `bg-gradient-to-r from-${colorClass}-400 to-${colorClass}-600`
            }`}>
              <IconComponent className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-[var(--text-primary)] truncate font-baloo">
                {activity.title}
              </h3>
              <p className="text-sm text-[var(--text-secondary)] capitalize">
                Focus Activity
              </p>
            </div>
          </div>
          
          {completed && (
            <div className="flex items-center space-x-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2">
              <Zap className="w-3 h-3" />
              <span>Done!</span>
            </div>
          )}
        </div>

        <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2 flex-1">
          {activity.description}
        </p>

        <div className="space-y-4 mt-auto">
          <div className="flex items-center justify-between text-sm text-[var(--text-muted)]">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>{formatDuration(activity.duration)}</span>
            </div>
            
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              theme.mode === 'high-contrast'
                ? `bg-[var(--accent-${difficultyColor})] text-black`
                : `bg-${difficultyColor}-100 text-${difficultyColor}-800`
            }`}>
              {activity.difficulty}
            </div>
            
            <div className="flex items-center space-x-1">
              <Zap className="w-3 h-3 text-orange-400" />
              <span>{activity.points_reward} pts</span>
            </div>
          </div>

          <button
            onClick={onStart}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-xl font-bold text-lg transition-all duration-200 font-baloo ${
              theme.reducedMotion ? '' : 'hover:scale-105'
            } ${
              completed
                ? theme.mode === 'high-contrast'
                  ? 'bg-gray-800 text-white hover:bg-black'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                : theme.mode === 'high-contrast'
                ? `bg-[var(--accent-${colorClass})] text-white hover:opacity-90`
                : `bg-gradient-to-r from-${colorClass}-400 to-${colorClass}-600 text-white hover:opacity-90`
            }`}
          >
            <Focus className="w-5 h-5 flex-shrink-0" />
            <span className="whitespace-nowrap">
              {completed ? 'Focus Again' : 'Start Focus'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}